import { selector } from 'recoil'
import { accountInfoState } from '@/recoil/AccountInfo'
import {
  marketTickerMapState,
  indexPriceMapState,
  markPriceMapState,
  futuresMarketOpenInterestMapState,
  futuresMarketInfoMapState
} from '@/recoil/Market'
import { recentTradesState } from '@/recoil/RecentTrades'
import { orderBookState } from '@/recoil/OrderBook'
import { accountWalletState } from '@/recoil/Wallet'
import { positionInfoState } from '@/recoil/Position'
import {
  notificationListState,
  popupNotificationListState
} from '@/recoil/Notification'

const accountInfoDataMap = {
  marginMode: 'account_mode',
  futuresLeverage: 'max_leverage',
  takerFeeRate: 'taker_fee_rate',
  makerFeeRate: 'maker_fee_rate',
  futuresTakerFeeRate: 'futures_taker_fee_rate',
  futuresMakerFeeRate: 'futures_maker_fee_rate'
}

export const dataAdapterState = selector({
  key: 'DataAdapter',
  get: () => {},
  set: ({ get, set, reset }, { id, event, topic, data, ts }) => {
    if (event === 'request') {
      if (id.includes('@trade')) {
        set(recentTradesState, {
          marketSymbol: id.replace('@trade', ''),
          recentTrades: data
        })
      } else if (id.includes('@orderbook')) {
        set(orderBookState, { newValue: data, isSnapshot: true })
      }
      return
    }

    if (topic) {
      if (topic === 'tickers') {
        const map = {}
        data.forEach(ticker => {
          map[ticker.symbol] = ticker
        })
        set(marketTickerMapState, map)
        return
      }

      if (topic === 'indexprices') {
        const map = {}
        data.forEach(({ symbol, price }) => {
          map[symbol] = price
        })
        set(indexPriceMapState, map)
        return
      }

      if (topic === 'markprices') {
        const map = {}
        data.forEach(({ symbol, price }) => {
          map[symbol] = price
        })
        set(markPriceMapState, map)
        return
      }

      if (topic.includes('openinterests')) {
        const map = {}
        data.forEach(({ symbol, openInterest }) => {
          map[symbol] = openInterest
        })
        set(futuresMarketOpenInterestMapState, map)
        return
      }

      if (topic === 'executionreport') {
        data.markRead = 0
        set(notificationListState, { notification: data })
        return
      }

      if (topic === 'account') {
        const accountInfo = get(accountInfoState)
        const newAccountInfo = {}
        Object.keys(data.accountDetail).forEach(key => {
          newAccountInfo[accountInfoDataMap[key]] = data.accountDetail[key]
        })
        if (
          accountInfo.max_leverage &&
          newAccountInfo.max_leverage &&
          accountInfo.max_leverage !== newAccountInfo.max_leverage
        ) {
          set(popupNotificationListState, {
            notification: {
              key: Date.now(),
              status: 'success',
              message: `Max account leverage Updated ${accountInfo.max_leverage}x -> ${newAccountInfo.max_leverage}x`
            }
          })
        }
        set(accountInfoState, {
          ...accountInfo,
          ...newAccountInfo
        })
        return
      }

      if (topic === 'balance') {
        set(accountWalletState, { tokenBalanceData: data.balances })
        return
      }

      if (topic.includes('@trade')) {
        data.ts = ts
        data.isNewest = true
        set(recentTradesState, { recentTrades: [data] })
        return
      }

      if (topic.includes('@orderbookupdate')) {
        set(orderBookState, { newValue: data })
        return
      }

      if (topic.includes('@estfundingrate')) {
        const futuresMarketInfoMap = JSON.parse(
          JSON.stringify(get(futuresMarketInfoMapState))
        )
        const { symbol, fundingRate, fundingTs } = data
        if (!futuresMarketInfoMap[symbol]) {
          futuresMarketInfoMap[symbol] = {}
        }
        futuresMarketInfoMap[symbol].est_funding_rate = fundingRate
        futuresMarketInfoMap[symbol].next_funding_time = fundingTs
        set(futuresMarketInfoMapState, futuresMarketInfoMap)
        return
      }

      if (topic === 'position') {
        const positionInfo = JSON.parse(JSON.stringify(get(positionInfoState)))
        const positionList = positionInfo.rows
        const newPositionList = []

        data.positions.forEach(
          ({
            symbol,
            positionQty,
            averageOpenPrice,
            costPosition,
            lastSumUnitaryFunding,
            pendingLongQty,
            pendingShortQty,
            settlePrice,
            pnl24H,
            fee24H,
            markPrice,
            estLiqPrice,
            imrwithOrders,
            mmrwithOrders,
            imr,
            mmr
          }) => {
            let position = positionList.find(({ symbol: s }) => symbol === s)
            if (!position) {
              position = {}
              newPositionList.push(position)
            }
            position.symbol = symbol
            position.position_qty = positionQty
            position.average_open_price = averageOpenPrice
            position.cost_position = costPosition
            position.last_sum_unitary_funding = lastSumUnitaryFunding
            position.pending_long_qty = pendingLongQty
            position.pending_short_qty = pendingShortQty
            position.settle_price = settlePrice
            position.pnl_24_h = pnl24H
            position.fee_24_h = fee24H
            position.mark_price = markPrice
            position.est_liq_price = estLiqPrice
            position.IMR_withdraw_orders = imrwithOrders
            position.MMR_with_orders = mmrwithOrders
            position.imr = imr
            position.mmr = mmr
          }
        )

        positionInfo.rows = positionList.concat(newPositionList)
        set(positionInfoState, positionInfo)
        return
      }
    }
  },
  cachePolicy_UNSTABLE: {
    eviction: 'most-recent'
  }
})
